// Concierge configuration - single source of truth for the 3-mode toggle.
//
// `mode` flips the widget UX between voice-only, chat-only, and multimodal.
// All three modes call the same ElevenLabs agent; the mode only changes
// which inputs/outputs the SDK exposes. See lib/voice for the mapping.

export type ConciergeMode = 'voice_only' | 'chat_only' | 'multimodal'

export interface ConciergeConfig {
  enabled: boolean
  mode: ConciergeMode
  /** ElevenLabs agent ID. Empty string disables the widget at runtime. */
  agentId: string
  /** Endpoint that returns a short-lived signed URL for the agent session. */
  signedUrlEndpoint: string
  /** Hard cap on a single voice session, seconds. */
  maxSessionDurationSec: number
  /** Hard cap on text messages per session. */
  maxMessagesPerSession: number
  /** Soft cap surfaced to the backend; backend enforces. */
  maxSessionsPerDay: number
}

const VALID_MODES: ReadonlySet<ConciergeMode> = new Set(['voice_only', 'chat_only', 'multimodal'])

const TRUE_VALUES = new Set(['true', '1', 'yes', 'on'])
const FALSE_VALUES = new Set(['false', '0', 'no', 'off'])

function parseBool(raw: string | undefined, fallback: boolean): boolean {
  /* v8 ignore start */
  if (raw === undefined) return fallback
  /* v8 ignore stop */
  const v = raw.trim().toLowerCase()
  if (TRUE_VALUES.has(v)) return true
  if (FALSE_VALUES.has(v)) return false
  return fallback
}

function parseMode(raw: string | undefined, fallback: ConciergeMode): ConciergeMode {
  /* v8 ignore start */
  if (!raw) return fallback
  /* v8 ignore stop */
  const v = raw.trim().toLowerCase() as ConciergeMode
  return VALID_MODES.has(v) ? v : fallback
}

/** Read the live config. Computed each call so tests can stub env between assertions. */
export function getConciergeConfig(): ConciergeConfig {
  /* v8 ignore start */
  const agentId = ((import.meta.env.VITE_ELEVENLABS_AGENT_ID as string | undefined) ?? '').trim()
  const signedUrlEndpoint = ((import.meta.env.VITE_CONCIERGE_SIGNED_URL_ENDPOINT as string | undefined) ?? '').trim()
  /* v8 ignore stop */
  return {
    enabled: parseBool(import.meta.env.VITE_CONCIERGE_ENABLED as string | undefined, false),
    mode: parseMode(import.meta.env.VITE_CONCIERGE_MODE as string | undefined, 'chat_only'),
    agentId,
    signedUrlEndpoint: signedUrlEndpoint.length > 0 ? signedUrlEndpoint : '/api/concierge/signed-url',
    maxSessionDurationSec: 300,
    maxMessagesPerSession: 20,
    maxSessionsPerDay: 200,
  }
}
